export const ORIGEN_LABELS: Record<string, string> = {
  MANUAL: 'Manual',
  EMAIL: 'Correo',
  CHAT: 'Chat',
  PHONE: 'Teléfono',
  WEB_FORM: 'Formulario web',
  PORTAL: 'Portal',
  SOCIAL: 'Redes sociales',
  API: 'API',
  SUB_TICKET: 'Sub-ticket',
  SPLIT: 'División',
  // Aliases por compatibilidad con valores antiguos:
  SUBTICKET: 'Sub-ticket',
  DIVISION: 'División',
};

/** Tipos de vínculo entre tickets (RF-08.x). */
export const LINK_TYPE_LABELS: Record<string, string> = {
  SUB_TICKET: 'Sub-ticket',
  MERGED_INTO: 'Fusionado en',
  SPLIT_FROM: 'Dividido de',
  RELATED: 'Relacionado',
};


export const OPERATOR_LABELS: Record<string, string> = {
  LIKE: 'Contiene',
  EQ: 'Igual a',
  NEQ: 'No igual a',
  IN: 'Está en',
  NOT_IN: 'No está en',
  GT: 'Mayor que',
  GTE: 'Mayor o igual que',
  LT: 'Menor que',
  LTE: 'Menor o igual que',
  IS_NULL: 'Es nulo',
  IS_NOT_NULL: 'No es nulo',
};


export const SCOPE_LABELS: Record<string, string> = {
  PRIVATE: 'Privado (solo yo)',
  PUBLIC: 'Público (todos)',
  TEAM: 'Equipo (mi equipo)',
};



export const PRIORIDAD_LABELS: Record<string, string> = {
  URGENT: 'Urgente',
  HIGH: 'Alta',
  MEDIUM: 'Media',
  LOW: 'Baja',
};

export const ESTADO_LABELS: Record<string, string> = {
  NEW: 'Nuevo',
  OPEN: 'Abierto',
  IN_PROGRESS: 'En Progreso',
  WAITING_CUSTOMER: 'En Espera (Cliente)',
  RESOLVED: 'Resuelto',
  CLOSED: 'Cerrado',
};


export const FIELD_LABELS: Record<string, string> = {
  owner: 'Asignado a',
  ownerUserId: 'Asignado a',
  creatorUserId: 'Creador',
  timeline: 'Línea de tiempo',
};

/** Reglas de escalada: disparador y acción (config de Tickets). */
export const ESCALATION_TRIGGER_LABELS: Record<string, string> = {
  SLA_BREACH: 'SLA vencido',
  SLA_WARNING: 'SLA por vencer',
  REOPEN_THRESHOLD: 'Reaperturas',
  MANUAL: 'Manual',
};

export const ESCALATION_ACTION_LABELS: Record<string, string> = {
  INCREASE_PRIORITY: 'Subir prioridad',
  REASSIGN_NEXT_LEVEL: 'Reasignar a usuario',
  NOTIFY_MANAGER: 'Notificar al responsable',
  CHANGE_PIPELINE: 'Cambiar de pipeline',
};

/** Tipos de campo personalizado de ticket. */
export const CUSTOM_FIELD_TYPE_LABELS: Record<string, string> = {
  TEXT: 'Texto',
  TEXTAREA: 'Texto largo',
  NUMBER: 'Número',
  DECIMAL: 'Decimal',
  DATE: 'Fecha',
  DATETIME: 'Fecha y hora',
  BOOLEAN: 'Sí / No',
  CHECKBOX: 'Casilla',
  SELECT: 'Lista desplegable',
  MULTISELECT: 'Selección múltiple',
  DROPDOWN: 'Lista desplegable',
  URL: 'Enlace',
  EMAIL: 'Correo',
  PHONE: 'Teléfono',
  USER: 'Usuario',
};

// 3. HELPERS

/** Humaniza un código crudo tipo WEB_FORM -> "Web form" si no está en ningún mapa. */
function humanize(value: string): string {
  return value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());
}

/** Formatea un valor de enum contra un mapa fijo, con fallback humanizador. */
export function formatEnum(value: string | null | undefined, map: Record<string, string>): string {
  if (!value) return '-';
  return map[value.toUpperCase()] ?? humanize(value);
}

// Atajos legibles para el template:
export const getOrigenLabel   = (v?: string | null) => formatEnum(v, ORIGEN_LABELS);
export const getLinkTypeLabel = (v?: string | null) => formatEnum(v, LINK_TYPE_LABELS);
export const getOperatorLabel = (v?: string | null) => formatEnum(v, OPERATOR_LABELS);
export const getScopeLabel    = (v?: string | null) => formatEnum(v, SCOPE_LABELS);
export const getPrioridadLabel= (v?: string | null) => formatEnum(v, PRIORIDAD_LABELS);
export const getEstadoLabel   = (v?: string | null) => formatEnum(v, ESTADO_LABELS);
export const getFieldLabel    = (v?: string | null) => formatEnum(v, FIELD_LABELS);
export const getEscalationTriggerLabel = (v?: string | null) => formatEnum(v, ESCALATION_TRIGGER_LABELS);
export const getEscalationActionLabel  = (v?: string | null) => formatEnum(v, ESCALATION_ACTION_LABELS);
export const getCustomFieldTypeLabel   = (v?: string | null) => formatEnum(v, CUSTOM_FIELD_TYPE_LABELS);


export function resolveConfigLabel(
  backendLabel: string | null | undefined,
  rawCode: string | null | undefined,
  fallbackMap: Record<string, string> = {},
): string {
  if (backendLabel && backendLabel.trim()) return backendLabel;
  return formatEnum(rawCode, fallbackMap);
}

export function getPlaceholder(label: string): string {
  return `-- Elegir ${label} --`;
}

// ─────────────────────────────────────────────────────────────
// 4. JERARQUÍA DE CATEGORÍAS (Inc 8)
//    Aplana el árbol padre-hijo para usar en un <select> plano,
//    marcando qué nodos tienen hijos (no deben ser seleccionables).
// ─────────────────────────────────────────────────────────────

export interface CategoryTreeNode {
  id: number;
  name: string;
  idParent: number | null;
  depth: number;
  hasChildren: boolean;
}

export function buildCategoryTree(
  items: { id: number; name: string; idParent?: number | null; sortOrder?: number }[]
): CategoryTreeNode[] {
  const byParent = new Map<number | null, typeof items>();
  const childCount = new Map<number, number>();

  for (const it of items) {
    const key = it.idParent ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)!.push(it);
    if (it.idParent != null) {
      childCount.set(it.idParent, (childCount.get(it.idParent) ?? 0) + 1);
    }
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }

  const out: CategoryTreeNode[] = [];
  const dfs = (parentId: number | null, depth: number) => {
    const children = byParent.get(parentId) ?? [];
    for (const c of children) {
      out.push({
        id: c.id,
        name: c.name,
        idParent: c.idParent ?? null,
        depth,
        hasChildren: (childCount.get(c.id) ?? 0) > 0
      });
      dfs(c.id, depth + 1);
    }
  };
  dfs(null, 0);
  return out;
}

/** Prefijo visual de indentación para nodos hijos en un <select> plano. */
export function categoryIndent(depth: number): string {
  return depth > 0 ? '\u00A0\u00A0'.repeat(depth) + '↳ ' : '';
}